import React, { forwardRef, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    Popover,
    Radio,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { Close, FilterAlt, TuneOutlined } from '@mui/icons-material';
import _ from 'lodash';

export const getFilterValues = (filters) => {
    const initialValues = {};

    filters.forEach((filter) => {
        initialValues[filter.id] = filter.default;
    });

    return initialValues;
};

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`
    };
}

const TabPanel = (props) => {
    const { filter, tabValue, value, index, multiple, options, radio, onChange, setResetState, resetState, ...other } = props;
    const [selected, setSelected] = useState(value ?? (radio ? null : []));

    useEffect(() => {
        setSelected(value ?? (radio ? null : []));
        setResetState(false);
    }, [value, resetState === true]);

    const [searchText, setSearchText] = useState('');

    const handleCheck = (id) => {
        if (multiple) {
            const idx = selected.findIndex((i) => i === id);
            if (idx < 0) {
                setSelected([...selected, id]);
            } else {
                setSelected((val) => val?.filter((i) => i !== id));
            }
        } else {
            setSelected(id);
        }
    };

    useEffect(() => {
        onChange(selected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const iterable = searchText ? options?.filter((i) => i?.name?.toLowerCase()?.includes(searchText.toLowerCase())) : options;

    return (
        <div
            role="tabpanel"
            hidden={tabValue !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            style={{ width: '82%', overflowY: 'scroll' }}
            {...other}
        >
            {tabValue === index && (
                <Box
                    sx={{
                        maxHeight: 300, // Limit height for scrolling
                        // Enable vertical scrolling
                        padding: 1,
                        width: '100%' // Ensure the container takes full width
                    }}
                >
                    <FormGroup>
                        {iterable?.map((i) => (
                            <Tooltip key={i.id} title={i.name} PopperProps={{ disablePortal: true }}>
                                <FormControlLabel
                                    control={
                                        radio ? (
                                            <Radio
                                                color="primary"
                                                size="small"
                                                checked={selected === i.id}
                                                onChange={() => handleCheck(i.id)}
                                                sx={{ mt: 0.2 }}
                                            />
                                        ) : (
                                            <Checkbox
                                                color="primary"
                                                size="small"
                                                checked={multiple ? selected?.includes(i.id) : selected === i.id}
                                                onChange={() => handleCheck(i.id)}
                                                sx={{ mt: 0.2 }}
                                            />
                                        )
                                    }
                                    label={`${i.name}${_.has(i, 'isActive') && !i?.isActive ? ' (Inactive)' : ''}`}
                                    sx={{
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        // display: 'block',
                                        margin: 0
                                    }}
                                />
                            </Tooltip>
                        ))}
                    </FormGroup>
                </Box>
            )}
        </div>
    );
};

const PreferenceFilter = ({ filterConfig, selectedValues, handleSetDefault, handleApplyFilter, handleResetFilter }) => {
    const [filterAnchor, setFilterAnchor] = useState(null);
    const [tabValue, setTabValue] = React.useState(0);
    const [filterInputs, setFilterInputs] = useState(selectedValues);
    const [resetState, setResetState] = useState(false);

    useEffect(() => {
        setFilterInputs(selectedValues);
    }, [selectedValues]);

    const handleFilterOpen = (e) => {
        setFilterAnchor(e.currentTarget);
    };
    const handleFilterClose = () => {
        setFilterAnchor(null);
        setTabValue(0);
    };

    const handleFilterReset = () => {
        setResetState(true);
        handleResetFilter();
    };

    const handleFilterDefault = () => {
        handleSetDefault(filterInputs);
        handleFilterClose();
    };

    const handleFilterSearch = () => {
        handleApplyFilter(filterInputs);
        handleFilterClose();
    };

    const handleChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleFilterChange = (key, value) => {
        const updateInputs = { ...filterInputs, [key]: value };
        setFilterInputs(updateInputs);
    };

    return (
        <>
            <Tooltip title="Preference">
                <IconButton color="primary" onClick={handleFilterOpen} sx={{ position: 'relative', ml: 2 }}>
                    <FilterAlt />
                </IconButton>
            </Tooltip>
            <Popover
                open={Boolean(filterAnchor)}
                anchorEl={filterAnchor}
                onClose={handleFilterClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                <Stack
                    direction="row"
                    sx={{
                        maxHeight: 50,
                        p: 1,
                        borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
                        justifyContent: 'space-between'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TuneOutlined sx={{ height: 24, width: 24 }} />
                        <Typography sx={{ ml: 1 }} variant="subtitle1">
                            Preference
                        </Typography>
                    </Box>
                    <IconButton onClick={handleFilterClose}>
                        <Close sx={{ height: 24, width: 24 }} />
                    </IconButton>
                </Stack>
                <Grid
                    container
                    sx={{
                        // maxWidth: 'calc(100vw / 2)',
                        minWidth: 450,
                        maxWidth: 450
                    }}
                >
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                flexGrow: 1,
                                bgcolor: 'background.paper',
                                display: 'flex',
                                height: 300,
                                '& .MuiTabs-flexContainer': { borderBottom: 'none' }
                            }}
                        >
                            <Tabs
                                orientation="vertical"
                                variant="scrollable"
                                value={tabValue}
                                onChange={handleChange}
                                aria-label="Vertical tabs example"
                                sx={{ borderRight: 1, borderColor: 'divider' }}
                            >
                                {filterConfig?.map((filter, index) => (
                                    <Tab
                                        key={filter?.id}
                                        label={filter?.label}
                                        {...a11yProps(`${index}`)}
                                        sx={{ alignItems: 'start !important' }}
                                    />
                                ))}
                            </Tabs>
                            {filterConfig?.map((filter, index) => (
                                <TabPanel
                                    key={index}
                                    tabValue={tabValue}
                                    value={selectedValues?.[filter?.id]}
                                    index={index}
                                    filter={filter}
                                    multiple={filter?.multiple}
                                    options={filter?.options || []}
                                    radio={filter?.radio}
                                    onChange={(val) => handleFilterChange(filter?.id, val)}
                                    setResetState={setResetState}
                                    resetState={resetState}
                                />
                            ))}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{
                                borderTop: (theme) => `1px solid ${theme.palette.grey[300]}`,
                                p: 1,
                                flexShrink: 0
                            }}
                        >
                            <Button size="small" variant="outlined" onClick={handleFilterReset}>
                                Reset
                            </Button>
                            <Stack direction="row" spacing={1}>
                                <Button size="small" variant="contained" onClick={handleFilterSearch}>
                                    Apply
                                </Button>
                                <Button size="small" variant="contained" onClick={handleFilterDefault}>
                                    Set as default
                                </Button>
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>
            </Popover>
        </>
    );
};

export default PreferenceFilter;
